import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "../api/apiClient";
import { Swiper, SwiperSlide } from "swiper/react";
import "aos/dist/aos.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useCart } from "./CartContext";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";

const ProduitsDetails = () => {
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState({
    product: true,
    relatedProducts: true,
    overall: true,
  });
  const [errors, setErrors] = useState({
    product: null,
    relatedProducts: null,
  });
  const { id_prod } = useParams();

  // Enhanced error handler
  const handleError = useCallback((error, type) => {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      `Erreur lors du chargement des ${type}`;

    setErrors((prev) => ({
      ...prev,
      [type]: errorMessage,
    }));

    if (error.response?.status >= 500) {
      toast.error(`Problème de connexion au serveur`, {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  }, []);

  // Fetch product details with enhanced error handling
  const fetchProductById = useCallback(async () => {
    if (!id_prod) return;

    try {
      setLoading((prev) => ({ ...prev, product: true }));
      setErrors((prev) => ({ ...prev, product: null }));

      const response = await apiClient.get(`/produits/${id_prod}`, {
        timeout: 10000,
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (response.data) {
        setProduct(response.data);
      } else {
        throw new Error("Produit non trouvé");
      }
    } catch (err) {
      handleError(err, "product");
    } finally {
      setLoading((prev) => ({ ...prev, product: false }));
    }
  }, [id_prod, handleError]);

  // Fetch all products with enhanced error handling
  const fetchProducts = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, relatedProducts: true }));
      setErrors((prev) => ({ ...prev, relatedProducts: null }));

      const response = await apiClient.get(`/produits`, {
        timeout: 10000,
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (response.data && Array.isArray(response.data)) {
        const filteredProducts = response.data.filter(
          (prod) => prod.id_prod !== parseInt(id_prod),
        );
        setProducts(filteredProducts);
      } else {
        throw new Error("Format de données invalide pour les produits");
      }
    } catch (err) {
      handleError(err, "relatedProducts");
    } finally {
      setLoading((prev) => ({ ...prev, relatedProducts: false }));
    }
  }, [id_prod, handleError]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading((prev) => ({ ...prev, overall: true }));
        await Promise.all([fetchProductById(), fetchProducts()]);
      } catch (error) {
      } finally {
        setLoading((prev) => ({ ...prev, overall: false }));
      }
    };

    fetchData();
  }, [fetchProductById, fetchProducts]);

  const increaseQuantity = () => setQuantity(quantity + 1);
  const decreaseQuantity = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  // Handle add to cart with quantity
  const handleAddToCart = () => {
    if (!product) return;

    try {
      for (let i = 0; i < quantity; i++) {
        addToCart({
          id_prod: product.id_prod,
          nom_prod: product.nom_prod,
          prix: product.prix,
          images: product.images,
        });
      }
      toast.success(
        <div>
          <strong>{product.nom_prod}</strong>
          <div>{quantity} x ajouté(s) au panier ✅</div>
        </div>,
        {
          position: "bottom-right",
          autoClose: 3000,
          theme: "light",
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    } catch (error) {
      toast.error("Erreur lors de l'ajout au panier", {
        position: "bottom-right",
      });
    }
  };

  // Loading component
  const LoadingSpinner = ({ message = "Chargement..." }) => (
    <div className="text-center py-5">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Chargement...</span>
      </div>
      <p className="mt-2 text-muted">{message}</p>
    </div>
  );

  // Error component
  const ErrorMessage = ({ message, onRetry }) => (
    <div className="text-center py-5">
      <div className="alert alert-warning" role="alert">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {message}
      </div>
      {onRetry && (
        <button className="btn btn-primary mt-3" onClick={onRetry}>
          <i className="fas fa-redo me-2"></i>
          Réessayer
        </button>
      )}
    </div>
  );

  // ProductCard component for related products
  const ProductCard = ({ produit }) => {
    const { addToCart } = useCart();

    const handleAddToCart = () => {
      try {
        addToCart({
          id_prod: produit.id_prod,
          nom_prod: produit.nom_prod,
          prix: produit.prix,
          images: produit.images,
        });
        toast.success(
          <div>
            <strong>{produit.nom_prod}</strong>
            <div>ajouté au panier ✅</div>
          </div>,
          {
            position: "bottom-right",
            autoClose: 3000,
            theme: "light",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
      } catch (error) {
        toast.error("Erreur lors de l'ajout au panier", {
          position: "bottom-right",
        });
      }
    };

    const defaultImage = "assets/images/products/feature-product-1.png";
    const imageUrl = produit.images?.[0]
      ? `${process.env.REACT_APP_API_URL}/${produit.images[0]}`
      : defaultImage;

    return (
      <div className="col-xl-3 col-lg-4 col-sm-6">
        <div className="product-item style-one mb-40">
          <div className="product-thumbnail">
            <img
              src={imageUrl}
              alt={produit.nom_prod}
              className="product-image"
              onError={(e) => {
                e.target.src = defaultImage;
              }}
            />

            <div className="hover-content">
              <Link to="#" className="icon-btn">
                <i className="fa fa-heart"></i>
              </Link>
              <Link
                to={imageUrl}
                className="img-popup icon-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa fa-eye"></i>
              </Link>
            </div>
            <div className="cart-button">
              <button
                className="cart-btn"
                onClick={handleAddToCart}
                disabled={!produit.prix}
              >
                <i className="far fa-shopping-basket"></i>{" "}
                <span className="text">Ajouter au panier</span>
              </button>
            </div>
          </div>
          <div className="product-info-wrap">
            <div className="product-info">
              <h4 className="title">
                <Link to={`/produitsDetails/${produit.id_prod}`}>
                  {produit.nom_prod || "Produit sans nom"}
                </Link>
              </h4>
              {produit.prix && (
                <div className="price">
                  <span className="current-price">{produit.prix} €</span>
                  {produit.ancien_prix && (
                    <span className="old-price">{produit.ancien_prix} €</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRelatedProducts = () => {
    if (loading.relatedProducts) {
      return <LoadingSpinner message="Chargement des produits similaires..." />;
    }

    if (errors.relatedProducts) {
      return (
        <ErrorMessage
          message={errors.relatedProducts}
          onRetry={fetchProducts}
        />
      );
    }

    if (!products.length) {
      return (
        <div className="text-center py-5">
          <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
          <p className="text-muted">Aucun autre produit disponible</p>
        </div>
      );
    }

    return (
      <div className="row justify-content-center">
        {products.slice(0, 8).map((produit) => (
          <ProductCard key={produit.id_prod} produit={produit} />
        ))}
      </div>
    );
  };

  // Main loading state
  if (loading.overall && (loading.product || loading.relatedProducts)) {
    return (
      <main className="main-bg">
        <div className="min-vh-50 d-flex justify-content-center align-items-center">
          <LoadingSpinner message="Chargement du produit..." />
        </div>
      </main>
    );
  }

  // Product error state
  if (errors.product) {
    return (
      <main className="main-bg">
        <div className="min-vh-50 d-flex justify-content-center align-items-center">
          <ErrorMessage message={errors.product} onRetry={fetchProductById} />
        </div>
      </main>
    );
  }

  // Product not found state
  if (!product && !loading.product) {
    return (
      <main className="main-bg">
        <div className="min-vh-50 d-flex justify-content-center align-items-center">
          <div className="text-center">
            <i className="fas fa-exclamation-circle fa-3x text-warning mb-3"></i>
            <h4>Produit non trouvé</h4>
            <p className="text-muted mb-4">
              Le produit que vous recherchez n'existe pas ou a été supprimé.
            </p>
            <Link to="/produits" className="btn btn-primary">
              <i className="fas fa-arrow-left me-2"></i>
              Retour aux produits
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const defaultProductImage = "assets/images/products/feature-product-1.png";
  const productImageUrl = product?.images?.[0]
    ? `/${product.images[0]}`
    : defaultProductImage;

  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>
          {product?.nom_prod
            ? `${product.nom_prod} | DVELGE Fourniture`
            : "Produit DVELGE Fourniture"}
        </title>
        <meta
          name="description"
          content={
            product?.description_prod ||
            "Découvrez nos produits DVELGE Fourniture"
          }
        />
        <meta
          property="og:title"
          content={
            product?.nom_prod
              ? `${product.nom_prod} | DVELGE Fourniture`
              : "Produit DVELGE Fourniture"
          }
        />
        <meta
          property="og:description"
          content={
            product?.description_prod ||
            "Découvrez nos produits DVELGE Fourniture"
          }
        />
        <meta property="og:image" content={productImageUrl} />
        <meta property="og:type" content="product" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <main className="main-bg">
        {/* Page banner */}
        <section className="page-banner">
          <div className="page-banner-wrapper p-r z-1">
            <div className="container">
              <div className="row">
                <div className="col-lg-6">
                  <div className="page-banner-content">
                    <h1>Détails du produit</h1>
                    <ul className="breadcrumb-link">
                      <li>
                        <Link to="/">Accueil</Link>
                      </li>
                      <li>
                        <i className="far fa-long-arrow-right"></i>
                      </li>
                      <li>
                        <Link to="/produits">Produits</Link>
                      </li>
                      <li>
                        <i className="far fa-long-arrow-right"></i>
                      </li>
                      <li className="active">{product?.nom_prod}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product details */}
        <section className="shop-details-section pt-120 pb-80">
          <div className="container">
            <div className="shop-details-wrapper">
              {loading.product ? (
                <div className="row">
                  <div className="col-12">
                    <LoadingSpinner message="Chargement des détails du produit..." />
                  </div>
                </div>
              ) : (
                <div className="row">
                  {/* Images */}
                  <div className="col-xl-6">
                    <div className="product-gallery-area mb-50">
                      {product?.images?.length > 0 ? (
                        <Swiper
                          spaceBetween={10}
                          slidesPerView={1}
                          autoplay={{ delay: 3000 }}
                          pagination={{ clickable: true }}
                          navigation
                          modules={[Navigation, Pagination, Autoplay]}
                        >
                          {product.images.map((img, index) => (
                            <SwiperSlide key={index}>
                              <img
                                src={`${process.env.REACT_APP_API_URL}/${img}`}
                                alt={`${product.nom_prod} - Image ${index + 1}`}
                                className="img-fluid rounded border"
                                onError={(e) => {
                                  e.target.src = defaultProductImage;
                                }}
                              />
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      ) : (
                        <div className="text-center py-5">
                          <img
                            src={defaultProductImage}
                            alt={product?.nom_prod}
                            className="img-fluid rounded border"
                          />
                          <p className="text-muted mt-3">
                            Aucune image disponible
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="col-xl-6">
                    <div className="product-info mb-50">
                      <h4 className="title">{product?.nom_prod}</h4>
                      <p className="text-muted mb-3">
                        {product?.description_prod ||
                          "Aucune description disponible."}
                      </p>

                      {product?.prix && (
                        <div className="product-price mb-4">
                          <h3 className="text-primary">{product.prix} €</h3>
                          {product.ancien_prix && (
                            <span className="text-muted text-decoration-line-through ms-2">
                              {product.ancien_prix} €
                            </span>
                          )}
                        </div>
                      )}

                      <div className="product-cart-variation">
                        <ul>
                          <li>
                            <div className="quantity-input">
                              <button
                                onClick={decreaseQuantity}
                                className="quantity-down"
                                disabled={quantity <= 1}
                              >
                                <i className="far fa-minus"></i>
                              </button>
                              <input
                                className="quantity"
                                type="text"
                                value={quantity}
                                readOnly
                              />
                              <button
                                onClick={increaseQuantity}
                                className="quantity-up"
                              >
                                <i className="far fa-plus"></i>
                              </button>
                            </div>
                          </li>
                          <li>
                            <button
                              className="theme-btn style-one"
                              onClick={handleAddToCart}
                              disabled={!product?.prix}
                            >
                              <i className="far fa-shopping-basket me-2"></i>
                              Ajouter au panier
                            </button>
                          </li>
                        </ul>
                      </div>

                      {/* Additional product info */}
                      <div className="product-meta mt-4">
                        {product?.id_prod && (
                          <p className="text-muted">
                            <small>Référence: {product.id_prod}</small>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related products */}
        <section className="releted-product-section pb-90">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-title text-center mb-50">
                  <h2>Produits Similaires</h2>
                  <p className="text-muted">
                    Découvrez d'autres produits qui pourraient vous intéresser
                  </p>
                </div>
              </div>
            </div>
            <div className="row">
              <div
                className="col-lg-12"
                data-aos="fade-up"
                data-aos-duration="1200"
              >
                {renderRelatedProducts()}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ProduitsDetails;
